import { useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
  Box,
  Grid,
  Button,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { camelCaseToSentenceCase } from "../../../helpers/camelCaseToSentenceCase";
import { useNavigate } from "react-router-dom";

enum TimeRange {
  Week = "Week",
  Month = "Month",
  Year = "Year",
}

type DateRange = {
  startDate: string;
  endDate: string;
};

type DriverStatusCount = {
  driverId?: string | null;
  driverName?: string;
  count: number;
};

type CameraCheckStatusSummary = {
  allGood: DriverStatusCount[];
  needsFix: DriverStatusCount[];
  noCamera: DriverStatusCount[];
  noActivity: DriverStatusCount[];
};

type LogbookCheckStatusSummary = {
  allGood: DriverStatusCount[];
  needsAttention: DriverStatusCount[];
  violation: DriverStatusCount[];
  overPersonalUse: DriverStatusCount[];
  dayOff: DriverStatusCount[];
};

type PreTripInspectionStatusSummary = {
  allGood: DriverStatusCount[];
  needsAttention: DriverStatusCount[];
  noPreTripInspection: DriverStatusCount[];
  offDuty: DriverStatusCount[];
  management: DriverStatusCount[];
  technicalIssues: DriverStatusCount[];
};

const formatDateInput = (date: Date) => date.toISOString().split("T")[0];

const createDefaultDateRange = (daysBack: number = 7): DateRange => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (daysBack - 1));

  return {
    startDate: formatDateInput(start),
    endDate: formatDateInput(end),
  };
};

const isValidDateRange = (range: DateRange) =>
  Boolean(range.startDate && range.endDate && range.startDate <= range.endDate);

const fetchTotalHarshEvents = async (
  range: string,
  token: string | null | undefined,
) => {
  const { data } = await axios.get(
    `${apiVariable}/totalHarshEventsForCurrent${range}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return data;
};

const fetchHarshEventsByType = async (
  range: string,
  token: string | null | undefined,
) => {
  const { data } = await axios.get(
    `${apiVariable}/harshEventsByTypeForCurrent${range}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return data;
};

const fetchTotalAccidents = async (
  range: string,
  token: string | null | undefined,
) => {
  const { data } = await axios.get(
    `${apiVariable}/totalAccidentsForCurrent${range}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return data;
};
const fetchLogbookChecksByStatus = async (
  range: string,
  token: string | null | undefined,
) => {
  const { data } = await axios.get(
    `${apiVariable}/logbookChecksByStatusForCurrent${range}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return data;
};

const fetchCameraCheckStatusSummary = async (
  range: DateRange,
  token: string | null | undefined,
) => {
  const { data } = await axios.get(
    `${apiVariable}/cameraChecks/statusSummary`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        startDate: range.startDate,
        endDate: range.endDate,
      },
    },
  );
  return data;
};

const fetchLogbookCheckStatusSummary = async (
  range: DateRange,
  token: string | null | undefined,
) => {
  const { data } = await axios.get(
    `${apiVariable}/logbookChecks/statusSummary`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        startDate: range.startDate,
        endDate: range.endDate,
      },
    },
  );
  return data;
};

const fetchPreTripInspectionStatusSummary = async (
  range: DateRange,
  token: string | null | undefined,
) => {
  const { data } = await axios.get(
    `${apiVariable}/preTripInspectionChecks/statusSummary`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        startDate: range.startDate,
        endDate: range.endDate,
      },
    },
  );
  return data;
};

const normalizeSummary = <T extends Record<string, DriverStatusCount[]>>(
  summary: T | undefined,
  keys: Array<keyof T>,
): Record<string, DriverStatusCount[]> => {
  if (!summary) {
    return keys.reduce<Record<string, DriverStatusCount[]>>((acc, key) => {
      acc[key as string] = [];
      return acc;
    }, {});
  }

  return keys.reduce<Record<string, DriverStatusCount[]>>((acc, key) => {
    const camelCaseKey = key as string;
    const pascalCaseKey =
      camelCaseKey.charAt(0).toUpperCase() + camelCaseKey.slice(1);
    acc[camelCaseKey] =
      (summary as Record<string, DriverStatusCount[]>)[camelCaseKey] ??
      (summary as Record<string, DriverStatusCount[]>)[pascalCaseKey] ??
      [];
    return acc;
  }, {});
};

type StatusConfig = {
  key: string;
  label: string;
  color: string;
};

const SafetyStatisticsCard = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [harshEventsRange, setHarshEventsRange] = useState<TimeRange>(
    TimeRange.Week,
  );
  const [accidentsRange, setAccidentsRange] = useState<TimeRange>(
    TimeRange.Week,
  );
  const [logbookRange, setLogbookRange] = useState<TimeRange>(TimeRange.Week);
  const [cameraSummaryRange, setCameraSummaryRange] = useState<DateRange>(
    createDefaultDateRange(),
  );
  const [logbookSummaryRange, setLogbookSummaryRange] = useState<DateRange>(
    createDefaultDateRange(),
  );
  const [preTripSummaryRange, setPreTripSummaryRange] = useState<DateRange>(
    createDefaultDateRange(),
  );

  const [selectedCameraStatus, setSelectedCameraStatus] =
    useState<string>("allGood");
  const [selectedLogbookStatus, setSelectedLogbookStatus] =
    useState<string>("allGood");
  const [selectedPreTripStatus, setSelectedPreTripStatus] =
    useState<string>("allGood");

  const [cameraVisibleCount, setCameraVisibleCount] = useState(10);
  const [logbookVisibleCount, setLogbookVisibleCount] = useState(10);
  const [preTripVisibleCount, setPreTripVisibleCount] = useState(10);

  const { data: harshEventsTotal, isLoading: isLoadingHarshEvents } = useQuery({
    queryKey: ["harshEventsTotal", harshEventsRange],
    queryFn: () => fetchTotalHarshEvents(harshEventsRange, token),
  });

  const { data: harshEventsByType, isLoading: isLoadingHarshEventsByType } =
    useQuery({
      queryKey: ["harshEventsByType", harshEventsRange],
      queryFn: () => fetchHarshEventsByType(harshEventsRange, token),
    });

  const { data: accidentsTotal, isLoading: isLoadingAccidents } = useQuery({
    queryKey: ["accidentsTotal", accidentsRange],
    queryFn: () => fetchTotalAccidents(accidentsRange, token),
  });

  const { data: logbookByStatus, isLoading: isLoadingLogbook } = useQuery({
    queryKey: ["logbookByStatus", logbookRange],
    queryFn: () => fetchLogbookChecksByStatus(logbookRange, token),
  });

  const { data: cameraCheckSummary, isLoading: isLoadingCameraSummary } =
    useQuery({
      queryKey: [
        "cameraCheckStatusSummary",
        cameraSummaryRange.startDate,
        cameraSummaryRange.endDate,
      ],
      queryFn: () =>
        fetchCameraCheckStatusSummary(
          cameraSummaryRange,
          token,
        ) as Promise<CameraCheckStatusSummary>,
      enabled: isValidDateRange(cameraSummaryRange),
    });

  const { data: logbookCheckSummary, isLoading: isLoadingLogbookSummary } =
    useQuery({
      queryKey: [
        "logbookCheckStatusSummary",
        logbookSummaryRange.startDate,
        logbookSummaryRange.endDate,
      ],
      queryFn: () =>
        fetchLogbookCheckStatusSummary(
          logbookSummaryRange,
          token,
        ) as Promise<LogbookCheckStatusSummary>,
      enabled: isValidDateRange(logbookSummaryRange),
    });

  const { data: preTripInspectionSummary, isLoading: isLoadingPreTripSummary } =
    useQuery({
      queryKey: [
        "preTripInspectionStatusSummary",
        preTripSummaryRange.startDate,
        preTripSummaryRange.endDate,
      ],
      queryFn: () =>
        fetchPreTripInspectionStatusSummary(
          preTripSummaryRange,
          token,
        ) as Promise<PreTripInspectionStatusSummary>,
      enabled: isValidDateRange(preTripSummaryRange),
    });

  const cameraStatusConfig: StatusConfig[] = useMemo(
    () => [
      { key: "allGood", label: "All Good", color: "#4caf50" },
      { key: "needsFix", label: "Needs Fix", color: "#ff9800" },
      { key: "noCamera", label: "No Camera", color: "#f44336" },
      { key: "noActivity", label: "No Activity", color: "#ec407a" },
    ],
    [],
  );

  const logbookStatusConfig: StatusConfig[] = useMemo(
    () => [
      { key: "allGood", label: "All Good", color: "#4caf50" },
      { key: "needsAttention", label: "Needs Attention", color: "#ff9800" },
      { key: "violation", label: "Violation", color: "#f44336" },
      {
        key: "overPersonalUse",
        label: "Over Personal Use",
        color: "#e91e63",
      },
      { key: "dayOff", label: "Day Off", color: "#2196f3" },
    ],
    [],
  );

  const preTripStatusConfig: StatusConfig[] = useMemo(
    () => [
      { key: "allGood", label: "All Good", color: "#4caf50" },
      { key: "needsAttention", label: "Needs Attention", color: "#ff9800" },
      {
        key: "noPreTripInspection",
        label: "No PTI",
        color: "#f44336",
      },
      { key: "offDuty", label: "Off Duty", color: "#2196f3" },
      {
        key: "management",
        label: "Management",
        color: "#0F172A",
      },
      {
        key: "technicalIssues",
        label: "Technical Issues",
        color: "#9c27b0",
      },
    ],
    [],
  );

  const normalizedCameraSummary = useMemo(
    () =>
      normalizeSummary<CameraCheckStatusSummary>(cameraCheckSummary, [
        "allGood",
        "needsFix",
        "noCamera",
        "noActivity",
      ]),
    [cameraCheckSummary],
  );

  const normalizedLogbookSummary = useMemo(
    () =>
      normalizeSummary<LogbookCheckStatusSummary>(logbookCheckSummary, [
        "allGood",
        "needsAttention",
        "violation",
        "overPersonalUse",
        "dayOff",
      ]),
    [logbookCheckSummary],
  );

  const normalizedPreTripSummary = useMemo(
    () =>
      normalizeSummary<PreTripInspectionStatusSummary>(
        preTripInspectionSummary,
        [
          "allGood",
          "needsAttention",
          "noPreTripInspection",
          "offDuty",
          "management",
          "technicalIssues",
        ],
      ),
    [preTripInspectionSummary],
  );

  const handleStatusChange = (
    statusKey: string,
    setter: (value: string) => void,
    resetVisibleCount: (value: number) => void,
  ) => {
    setter(statusKey);
    resetVisibleCount(10);
  };

  const renderStatusSummaryCard = (
    title: string,
    range: DateRange,
    setRange: (value: DateRange) => void,
    statusConfig: StatusConfig[],
    selectedStatus: string,
    setSelectedStatus: (value: string) => void,
    isLoading: boolean,
    summary: Record<string, DriverStatusCount[]>,
    visibleCount: number,
    setVisibleCount: (value: number) => void,
    onDriverClick?: (driver: DriverStatusCount) => void,
  ) => {
    const drivers = (summary[selectedStatus] ?? []).slice();
    const sortedDrivers = drivers.sort((a, b) => b.count - a.count);
    const displayedDrivers = sortedDrivers.slice(0, visibleCount);
    const rangeIsInvalid = !isValidDateRange(range);

    return (
      <Card
        sx={{
          boxShadow: 3,
          borderRadius: 2,
          backgroundColor: "#ffffff",
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <br />
        <Box
          display="flex"
          gap={1}
          alignItems="center"
          justifyContent="flex-end"
          flexWrap="wrap"
        >
          <TextField
            label="Start"
            type="date"
            size="small"
            value={range.startDate}
            onChange={(e) => {
              const updatedRange = {
                ...range,
                startDate: e.target.value,
              };
              setRange(updatedRange);
              setVisibleCount(10);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 140 }}
          />
          <TextField
            label="End"
            type="date"
            size="small"
            value={range.endDate}
            onChange={(e) => {
              const updatedRange = {
                ...range,
                endDate: e.target.value,
              };
              setRange(updatedRange);
              setVisibleCount(10);
            }}
            InputLabelProps={{ shrink: true }}
            error={rangeIsInvalid}
            sx={{ minWidth: 140, mr: 1 }}
            helperText={
              rangeIsInvalid
                ? "Start date must be on or before end date"
                : undefined
            }
          />
        </Box>
        <CardHeader
          className="responsive-card-header"
          title={title}
          titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
          // action={

          // }
        />
        <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {isLoading ? (
            <Typography>Loading...</Typography>
          ) : (
            <Grid container spacing={2} sx={{ flex: 1 }}>
              <Grid item xs={12} md={4}>
                <Box display="flex" flexDirection="column" gap={1}>
                  {statusConfig.map((status) => {
                    const isSelected = selectedStatus === status.key;
                    return (
                      <Box key={status.key} sx={{ display: "inline-flex" }}>
                        <Tooltip title={status.label} arrow>
                          <Box
                            role="button"
                            aria-label={status.label}
                            onClick={() =>
                              handleStatusChange(
                                status.key,
                                setSelectedStatus,
                                setVisibleCount,
                              )
                            }
                            sx={{
                              width: 16,
                              height: 16,
                              backgroundColor: status.color,
                              borderRadius: "4px",
                              cursor: "pointer",
                              outline: isSelected ? "2px solid" : "none",
                              outlineColor: "primary.main",
                            }}
                          />
                        </Tooltip>
                      </Box>
                    );
                  })}
                </Box>
              </Grid>
              <Grid item xs={12} md={8} display="flex" flexDirection="column">
                <Box
                  sx={{
                    maxHeight: 260,
                    overflowY: "auto",
                    pr: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {displayedDrivers.length > 0 ? (
                    displayedDrivers.map((driver, index) => {
                      const driverKey = `${
                        driver.driverId ?? driver.driverName ?? "unknown"
                      }-${index}`;
                      const driverName = driver.driverName?.trim();
                      const isNavigable = Boolean(onDriverClick && driverName);

                      return (
                        <Box
                          key={driverKey}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          gap={1.5}
                        >
                          {isNavigable ? (
                            <Box
                              component="button"
                              type="button"
                              onClick={() => onDriverClick?.(driver)}
                              sx={{
                                border: "none",
                                background: "none",
                                padding: 0,
                                margin: 0,
                                textAlign: "left",
                                cursor: "pointer",
                                color: "primary.main",
                                fontSize: "0.875rem",
                                fontFamily: "inherit",
                                lineHeight: 1.43,
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                "&:hover": {
                                  textDecoration: "underline",
                                },
                                "&:focus-visible": {
                                  outline: "2px solid",
                                  outlineColor: "primary.main",
                                  outlineOffset: 2,
                                },
                              }}
                            >
                              {driverName}
                            </Box>
                          ) : (
                            <Typography variant="body2" noWrap>
                              {driver.driverName || "Unknown Driver"}
                            </Typography>
                          )}
                          <Typography variant="body2" fontWeight="bold">
                            {driver.count} d
                          </Typography>
                        </Box>
                      );
                    })
                  ) : (
                    <Typography>No data available</Typography>
                  )}
                </Box>
                {visibleCount < sortedDrivers.length && (
                  <Button
                    onClick={() => setVisibleCount(visibleCount + 10)}
                    sx={{ mt: 2, alignSelf: "flex-start" }}
                  >
                    Load More
                  </Button>
                )}
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
    );
  };

  const handleDriverNavigation = (path: string, driver: DriverStatusCount) => {
    const driverName = driver.driverName?.trim();
    const search = driverName
      ? `?driver=${encodeURIComponent(driverName)}`
      : "";

    navigate({ pathname: path, search });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <Grid container spacing={4} maxWidth="lg">
        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              backgroundColor: "#ffffff",
              textAlign: "center",
              flex: 1,
            }}
          >
            <CardHeader
              className="responsive-card-header"
              title="Total Harsh Events"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
              action={
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={harshEventsRange}
                    onChange={(e) =>
                      setHarshEventsRange(e.target.value as TimeRange)
                    }
                  >
                    <MenuItem value={TimeRange.Week}>1 Week</MenuItem>
                    <MenuItem value={TimeRange.Month}>1 Month</MenuItem>
                    <MenuItem value={TimeRange.Year}>1 Year</MenuItem>
                  </Select>
                </FormControl>
              }
            />
            <CardContent>
              {isLoadingHarshEvents ? (
                <Typography>Loading...</Typography>
              ) : (
                <Typography variant="h4" color="primary">
                  {harshEventsTotal}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              backgroundColor: "#ffffff",
              textAlign: "center",
              flex: 1,
            }}
          >
            <CardHeader
              className="responsive-card-header"
              title="Total Accidents"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
              action={
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={accidentsRange}
                    onChange={(e) =>
                      setAccidentsRange(e.target.value as TimeRange)
                    }
                  >
                    <MenuItem value={TimeRange.Week}>1 Week</MenuItem>
                    <MenuItem value={TimeRange.Month}>1 Month</MenuItem>
                    <MenuItem value={TimeRange.Year}>1 Year</MenuItem>
                  </Select>
                </FormControl>
              }
            />
            <CardContent>
              {isLoadingAccidents ? (
                <Typography>Loading...</Typography>
              ) : (
                <Typography variant="h4" color="primary">
                  {accidentsTotal}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              backgroundColor: "#ffffff",
              textAlign: "center",
              flex: 1,
            }}
          >
            <CardHeader
              className="responsive-card-header"
              title="Harsh Events by Type"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
              action={
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={harshEventsRange}
                    onChange={(e) =>
                      setHarshEventsRange(e.target.value as TimeRange)
                    }
                  >
                    <MenuItem value={TimeRange.Week}>1 Week</MenuItem>
                    <MenuItem value={TimeRange.Month}>1 Month</MenuItem>
                    <MenuItem value={TimeRange.Year}>1 Year</MenuItem>
                  </Select>
                </FormControl>
              }
            />
            <CardContent>
              {isLoadingHarshEventsByType ? (
                <Typography>Loading...</Typography>
              ) : harshEventsByType && harshEventsByType.length > 0 ? (
                harshEventsByType.map((item: any) => (
                  <Typography key={item.harshEventType}>
                    {`${camelCaseToSentenceCase(item.harshEventType)}: ${
                      item.totalHarshEvents
                    }`}
                  </Typography>
                ))
              ) : (
                <Typography>No data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              backgroundColor: "#ffffff",
              textAlign: "center",
              flex: 1,
            }}
          >
            <CardHeader
              className="responsive-card-header"
              title="Logbook Checks by Status"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
              action={
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={logbookRange}
                    onChange={(e) =>
                      setLogbookRange(e.target.value as TimeRange)
                    }
                  >
                    <MenuItem value={TimeRange.Week}>1 Week</MenuItem>
                    <MenuItem value={TimeRange.Month}>1 Month</MenuItem>
                    <MenuItem value={TimeRange.Year}>1 Year</MenuItem>
                  </Select>
                </FormControl>
              }
            />
            <CardContent>
              {isLoadingLogbook ? (
                <Typography>Loading...</Typography>
              ) : logbookByStatus && logbookByStatus.length > 0 ? (
                logbookByStatus.map((item: any) => (
                  <Typography key={item.status}>
                    {`${camelCaseToSentenceCase(item.status)}: ${item.total}`}
                  </Typography>
                ))
              ) : (
                <Typography>No data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          {renderStatusSummaryCard(
            "Camera Check Status",
            cameraSummaryRange,
            setCameraSummaryRange,
            cameraStatusConfig,
            selectedCameraStatus,
            setSelectedCameraStatus,
            isLoadingCameraSummary,
            normalizedCameraSummary,
            cameraVisibleCount,
            setCameraVisibleCount,
            (driver) => handleDriverNavigation("/safety/cameraChecks", driver),
          )}
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          {renderStatusSummaryCard(
            "Logbook Check Status",
            logbookSummaryRange,
            setLogbookSummaryRange,
            logbookStatusConfig,
            selectedLogbookStatus,
            setSelectedLogbookStatus,
            isLoadingLogbookSummary,
            normalizedLogbookSummary,
            logbookVisibleCount,
            setLogbookVisibleCount,
            (driver) => handleDriverNavigation("/safety/logbookChecks", driver),
          )}
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          {renderStatusSummaryCard(
            "Pre-Trip Inspection Status",
            preTripSummaryRange,
            setPreTripSummaryRange,
            preTripStatusConfig,
            selectedPreTripStatus,
            setSelectedPreTripStatus,
            isLoadingPreTripSummary,
            normalizedPreTripSummary,
            preTripVisibleCount,
            setPreTripVisibleCount,
            (driver) =>
              handleDriverNavigation("/safety/preTripInspectionChecks", driver),
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default SafetyStatisticsCard;
